import React from 'react';
import styled, { css } from 'styled-components';
import { Stack } from '@koob/margaret';
import { daysBetween } from '@koob/dates';

const HotelPic = styled.div`
  background-image: url('${(props) => props.image}');
  border-radius: 20px;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  height: 145px;
`;

const TitleCard = styled.div`
  font-weight: 800;
  font-size: 12px;
  text-transform: uppercase;
`;

const NormalContent = styled.div`
  margin-top: 0px !important;
  display: flex;
  align-items: center;
`;

const LightContent = styled.div`
  color: ${({ theme }) => theme.textLighter};
  flex: 1;
`;

const HotelName = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-top: ${({ theme }) => theme.spacing()};
`;

const HotelAdress = styled.div`
  font-size: 14px;
  font-weight: 400;
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

export const WrapperPrice = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 0;
`;

export const TotalPrice = styled.div`
  text-transform: uppercase;
  font-weight: bold;

  ${(props) =>
    props.orange &&
    css`
      color: orange;
    `}
`;

export const WithoutPromo = styled.div`
  text-transform: uppercase;
  text-decoration: line-through;
  margin-left: 15px;
`;

export default function CheckoutHotelRoomSelectionSummary({
  hotel,
  startDate,
  endDate,
  rooms,
  currentSelectedRoom,
  totalRoomNumber,
  disabledButton,
  roomsFilter,
  handleNextStep,
  children,
  translations,
  placeholderImage,
  renderButton,
  renderRoomPromotion,
  renderRoomRequiredPromotion,
  renderGrid,
  renderCell,
  formatCurrency,
  formatDate,
}) {
  const hotelName = hotel?.displayName;
  const hotelAddress = hotel?.address;
  const img = hotel?.primaryAttachment?.imageUrl;
  const nights = daysBetween(startDate, endDate);

  const getButtonLabel = () => {
    return rooms?.length === totalRoomNumber - 1 && currentSelectedRoom
      ? translations.validate
      : translations.continueSelection;
  };

  let originalTotalPriceWithoutPromo = 0;
  let originalTotalPriceWithPromo = 0;
  let currency = undefined;

  if (
    !originalTotalPriceWithPromo &&
    ((rooms && rooms.length > 0) || currentSelectedRoom)
  ) {
    if (currentSelectedRoom) {
      originalTotalPriceWithPromo += parseFloat(
        currentSelectedRoom?.bed?.totalPriceWithPromo,
      );
      originalTotalPriceWithoutPromo += parseFloat(
        currentSelectedRoom?.bed?.totalPriceWithoutPromo,
      );
      currency = currentSelectedRoom?.bed?.currency;
    }
    if (rooms) {
      rooms.forEach((room) => {
        originalTotalPriceWithPromo += parseFloat(
          room?.bed?.totalPriceWithPromo,
        );
        originalTotalPriceWithoutPromo += parseFloat(
          room?.bed?.totalPriceWithoutPromo,
        );
        currency = room?.bed?.currency;
      });
    }
  }

  const totalTravelers = Math.max(
    roomsFilter?.reduce((acc, curr) => {
      acc += curr.adults;
      acc += curr.children;
      return acc;
    }, 0),
    1,
  );

  const totalChildTravelers = roomsFilter?.reduce((acc, curr) => {
    acc += curr.children;
    return acc;
  }, 0);

  const Grid = renderGrid || 'div';
  const Cell = renderCell || 'div';

  return (
    <div className="bg-white rounded-2xl py-5 px-6 border border-solid border-k-gray">
      <div>
        <Grid>
          <Cell sizes={{ default: 1, tabletprod: 1 / 2 }}>
            {Boolean(img) ? (
              <HotelPic image={img} alt="" />
            ) : (
              <HotelPic image={placeholderImage} alt="" />
            )}
          </Cell>

          <Cell sizes={{ default: 1, tabletprod: 1 / 2 }}>
            <HotelName>{hotelName}</HotelName>

            <HotelAdress>{hotelAddress}</HotelAdress>
          </Cell>
        </Grid>

        <div className="pb-3 flex-col space-y-3">
          <TitleCard>{translations.stay}</TitleCard>

          {startDate && endDate && (
            <NormalContent direction="row">
              {formatDate(startDate, 'DD/MM/YYYY')}{' '}
              <i
                className="fa-light fa-arrow-right"
                style={{ fontSize: 40, color: '#D6D6D6' }}
              />{' '}
              {formatDate(endDate, 'DD/MM/YYYY')}
            </NormalContent>
          )}

          {startDate && endDate && (
            <Stack
              alignY="center"
              gutterSize={0.5}
              style={{ marginTop: '0px' }}
            >
              <i className="fa-light fa-moon" style={{ fontSize: 18 }} />
              <span>{translations.nNightsStay(nights, nights + 1)}</span>
            </Stack>
          )}

          {roomsFilter && (
            <div className="mt-1 py-2 px-5 text-gray-500 border rounded flex justify-between items-center">
              <div>
                <i className="far fa-person mr-1.5" />
                {translations.nAdults(totalTravelers - totalChildTravelers)}
              </div>

              <div>
                <i className="far fa-child-reaching mr-1.5" />
                {translations.nChildren(totalChildTravelers)}
              </div>

              <div>
                <i className="far fa-bed-bunk mr-1.5" />
                {translations.nRooms(roomsFilter.length)}
              </div>
            </div>
          )}

          {((rooms && rooms.length > 0) || currentSelectedRoom) && (
            <TitleCard>{translations.roomtitle}</TitleCard>
          )}

          {rooms && (
            <>
              {rooms.map((room, descriptionKey) => (
                <div key={descriptionKey}>
                  <div
                    key={descriptionKey}
                    className="flex justify-between items-center"
                  >
                    <LightContent>
                      {room.name} {room?.bed?.name ? `(${room.bed.name})` : ''}
                    </LightContent>

                    <div className="text-sm font-medium text-right">
                      {currency && (
                        <div>
                          <div className="text-orange-600">
                            {formatCurrency({
                              amount: room?.bed?.totalPriceWithPromo,
                              currency: room.bed.currency,
                            })}
                          </div>

                          <div className="text-xs text-gray-600">
                            {formatCurrency({
                              amount:
                                room?.bed?.totalPriceWithPromo / totalTravelers,
                              currency: room.bed.currency,
                            })}{' '}
                            / PAX
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {(room?.bed?.promotions?.length > 0 ||
                    room?.bed?.mandatorySupplements?.length > 0) && (
                    <div className="flex flex-wrap gap-1">
                      {room?.bed?.promotions?.map((name, index) =>
                        renderRoomPromotion({
                          key: index,
                          name,
                          room,
                          bed: room.bed,
                        }),
                      )}

                      {room?.optionalPromos?.map(({ displayName }, index) =>
                        renderRoomPromotion({
                          key: `supp_${index}`,
                          name: displayName,
                          room,
                          bed: room.bed,
                        }),
                      )}

                      {room?.bed?.mandatorySupplements?.map((name, index) =>
                        renderRoomRequiredPromotion({
                          key: index,
                          name,
                          label: translations.requiredSupplement,
                        }),
                      )}
                    </div>
                  )}
                </div>
              ))}

              {currentSelectedRoom && (
                <div className="flex justify-between items-center">
                  <LightContent>
                    {currentSelectedRoom.name}{' '}
                    {currentSelectedRoom?.bed?.name
                      ? `(${currentSelectedRoom.bed.name})`
                      : ''}
                  </LightContent>

                  <div className="text-sm font-medium text-right">
                    {currency && (
                      <div>
                        <div className="text-orange-600">
                          {formatCurrency({
                            amount:
                              currentSelectedRoom?.bed?.totalPriceWithPromo,
                            currency: currentSelectedRoom?.bed?.currency,
                          })}
                        </div>

                        <div className="text-xs text-gray-600">
                          {formatCurrency({
                            amount:
                              currentSelectedRoom?.bed?.totalPriceWithPromo /
                              totalTravelers,
                            currency: currentSelectedRoom?.bed?.currency,
                          })}{' '}
                          / PAX
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {currentSelectedRoom?.bed?.name &&
            ((currentSelectedRoom?.bed?.promotions?.length > 0 &&
              rooms.length < roomsFilter.length) ||
              (currentSelectedRoom?.bed?.mandatorySupplements?.length > 0 &&
                rooms.length < roomsFilter.length) ||
              (currentSelectedRoom?.optionalPromos?.length > 0 &&
                rooms.length < roomsFilter.length)) && (
              <div className="flex flex-wrap gap-1">
                {currentSelectedRoom?.bed?.promotions?.map((name, index) => {
                  if (
                    name !== null &&
                    !currentSelectedRoom?.bed?.optionalSupplements?.some(
                      (item) => item?.name === name,
                    )
                  ) {
                    return renderRoomPromotion({
                      key: index,
                      name,
                      room: currentSelectedRoom,
                      bed: currentSelectedRoom.bed,
                    });
                  }
                })}
                {currentSelectedRoom?.optionalPromos?.map(
                  ({ displayName }, index) =>
                    renderRoomPromotion({
                      key: `supp_${index}`,
                      name: displayName,
                      room: currentSelectedRoom,
                      bed: currentSelectedRoom.bed,
                    }),
                )}

                {currentSelectedRoom?.bed?.mandatorySupplements?.map(
                  (name, index) =>
                    renderRoomRequiredPromotion({
                      key: index,
                      name,
                      label: translations.requiredSupplement,
                    }),
                )}
              </div>
            )}

          <Stack direction="column" style={{ marginTop: '20px' }}>
            <div style={{ width: '100%', marginTop: 0 }}>
              {Boolean(originalTotalPriceWithPromo) && (
                <>
                  <WrapperPrice style={{ marginTop: 0 }}>
                    <TotalPrice>{translations.total} :</TotalPrice>

                    {originalTotalPriceWithoutPromo !==
                      originalTotalPriceWithPromo && (
                      <WithoutPromo bold className="w-fit">
                        {formatCurrency({
                          amount: originalTotalPriceWithoutPromo,
                          currency: currency,
                        })}
                      </WithoutPromo>
                    )}

                    <div className="font-semibold text-right">
                      {!currency ? (
                        0
                      ) : (
                        <div>
                          <div className="text-orange-600">
                            {formatCurrency({
                              amount: originalTotalPriceWithPromo,
                              currency: currency,
                            })}
                          </div>

                          <div className="text-xs text-gray-600">
                            {formatCurrency({
                              amount:
                                originalTotalPriceWithPromo / totalTravelers,
                              currency: currency,
                            })}{' '}
                            / PAX
                          </div>
                        </div>
                      )}
                    </div>
                  </WrapperPrice>
                </>
              )}
            </div>
          </Stack>
        </div>

        {children && children}

        {renderButton && (
          <div style={{ marginTop: '10px' }}>
            {renderButton({
              onClick: handleNextStep,
              disabled: disabledButton,
              label: getButtonLabel(),
              testId: 'confirm-room',
            })}
          </div>
        )}
      </div>
    </div>
  );
}
